import 'react-json-view-lite/dist/index.css';

import { faBug } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/button/Button';
import { CopyButton } from 'components/copy/CopyButton';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { usePermissions } from 'contexts/PermissionsContext';
import { usePopup } from 'custom_hooks/usePopup';
import { memo } from 'react';
import {
    darkStyles,
    defaultStyles,
    JsonView,
    Props as JsonViewerProps,
} from 'react-json-view-lite';
import { useDebug } from 'shared/debug/debug_store';
import styled from 'styled-components';
import { ThemeType } from 'styles/colorMode';
import { useColorMode } from 'styles/theme';
import { tokens } from 'styles/tokens';
import { stopEvent } from 'utils/event';
import { MouseOrKeyboardEvent } from 'utils/types';

type JsonViewerData = JsonViewerProps['data'];

const IdWrapper = styled.div`
    display: flex;
    align-items: baseline;
    gap: 1ch;
`;

const IdValue = styled.pre`
    border-radius: 0.45rem;
    background-color: ${tokens.background.subtle};
    border: 1px solid ${tokens.nav.border};
    padding: 0.25rem 0.5rem;
    margin: 0;
`;

function extractNamedValues(
    value: JsonViewerData,
    ...keys: string[]
): [string, number | string][] {
    const result: [string, number | string][] = [];
    for (const key of keys) {
        if (key in value) {
            // @ts-expect-error TS is not powerful enough to recognize we just checked this
            const v: unknown = value[key];
            if (typeof v === 'string' || typeof v === 'number') {
                result.push([key, v]);
            }
        }
    }
    return result;
}

/**
 * A header to render atop the JSON data, allowing easy access to common
 * properties, like the Id
 */
function KnownInfoHeader({ value }: { value: JsonViewerData }) {
    const vals = extractNamedValues(value, 'Id', 'id');
    if (vals.length === 0) {
        return <></>;
    }

    return (
        <div className="flex flex-col gap-5 mb-5">
            {vals.map(([key, val]) => (
                <IdWrapper key={key}>
                    <span>{key}:</span>
                    <IdValue>{val}</IdValue>
                    <CopyButton text={val.toString()} />
                </IdWrapper>
            ))}
        </div>
    );
}

export type DebugValueProps = Readonly<{
    value: JsonViewerProps['data'];
    description: string;
    className?: string;
}>;

function DebugValueTrigger({ value, description, className }: DebugValueProps) {
    const colorMode = useColorMode();
    const isDark = colorMode === ThemeType.dark;
    const { close, toggleOpen, popupRef, triggerRef, isOpen } = usePopup();

    const onOpenPopup = (event: MouseOrKeyboardEvent<HTMLButtonElement>) => {
        stopEvent(event);
        toggleOpen();
    };

    const title = `Inspect ${description}`;

    return (
        <>
            <Button
                ref={triggerRef}
                className={`px-5 ${className}`}
                onClick={onOpenPopup}
                title={title}
                aria-label={title}
            >
                <FontAwesomeIcon icon={faBug} color={tokens.warning.default} />
            </Button>
            {isOpen && (
                <StatelessPopup
                    popupClassName="modal-popup"
                    noPadding
                    ref={popupRef}
                    anchor={triggerRef.current}
                    onClose={close}
                >
                    <div
                        style={{
                            maxHeight: '75vh',
                            minWidth: '50rem',
                            overflowY: 'auto',
                            padding: '1rem',
                            fontSize: '14px',
                        }}
                    >
                        <KnownInfoHeader value={value} />
                        <JsonView
                            data={value}
                            style={isDark ? darkStyles : defaultStyles}
                        />
                    </div>
                </StatelessPopup>
            )}
        </>
    );
}

function DebugValue({ value, description, className }: DebugValueProps) {
    const { canViewDebug } = usePermissions();
    const { isDebugMode } = useDebug();

    return (
        <>
            {canViewDebug && isDebugMode && (
                <DebugValueTrigger
                    description={description}
                    value={value}
                    className={className}
                />
            )}
        </>
    );
}

const memoDebugValue = memo(DebugValue);
export { memoDebugValue as DebugValue };
